import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, History, Trash2, Sparkles, Wrench, Rocket } from "lucide-react";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();

const TAG_STYLE = {
  feature: { label: "Feature", className: "bg-emerald-100 text-emerald-700", Icon: Rocket },
  enhancement: { label: "Enhancement", className: "bg-sky-100 text-sky-700", Icon: Sparkles },
  bugfix: { label: "Bug fix", className: "bg-amber-100 text-amber-700", Icon: Wrench },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function UpdatesAdmin() {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/updates`, { withCredentials: true });
      setUpdates(data.updates || []);
    } catch { toast.error("Failed to load updates"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    if (!window.confirm("Delete this update log entry?")) return;
    try {
      await axios.delete(`${API}/admin/updates/${u.update_id}`, { withCredentials: true });
      setUpdates((prev) => prev.filter((row) => row.update_id !== u.update_id));
      toast.success("Entry deleted");
    } catch { toast.error("Failed to delete entry"); }
  };

  return (
    <div className="space-y-6" data-testid="updates-admin-section">
      <div className="flex items-center gap-2">
        <History className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-lg font-semibold">Updates</h2>
        <span className="text-sm text-muted-foreground">— a log of what the dev agent has built or fixed, most recent first</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : updates.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm" data-testid="updates-empty-state">
          No updates logged yet.
        </div>
      ) : (
        <div className="relative pl-6 space-y-6 before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-border">
          {updates.map((u) => (
            <div key={u.update_id} className="relative" data-testid={`update-entry-${u.update_id}`}>
              <span className="absolute -left-6 top-1.5 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
              <div className="bg-white border border-border rounded-lg p-4 group">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium text-sm" data-testid={`update-title-${u.update_id}`}>{u.title}</h3>
                      {(u.tags || []).map((tag) => {
                        const meta = TAG_STYLE[tag] || TAG_STYLE.feature;
                        const Icon = meta.Icon;
                        return (
                          <span key={tag} className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${meta.className}`}>
                            <Icon className="h-2.5 w-2.5" />{meta.label}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</p>
                    {u.summary && (
                      <p className="text-sm text-foreground/80 whitespace-pre-line">{u.summary}</p>
                    )}
                  </div>
                  <button
                    onClick={() => remove(u)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-red-600 shrink-0"
                    data-testid={`update-delete-btn-${u.update_id}`}
                    title="Delete this entry"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
